import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import MenuTabs from "../../menus/MenuTabs";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormField } from "src/Components/ui/form";
import FormFieldItem from "src/common/form/FormFieldItem";
import { Input } from "src/Components/ui/input";
import { toast } from "react-toastify";
import { DialogClose } from "src/Components/ui/dialog";
import { Button } from "src/Components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../ui/card";
import { List, DollarSign } from "lucide-react";
import { Separator } from "../../../ui/separator";

function SubOptionForm(props) {
  const { t } = useTranslation();

  const dialogCloseRef = useRef(null);

  const formSchema = z.object({
    name_ar: z
      .string({
        required_error: `${t("gloabl.name")} ${t(
          "errorMessage.fieldRequired"
        )}.`,
      })
      .min(2, {
        message: `${t("gloabl.name")} ${t("errorMessage.fieldCharsLength")}.`,
      }),
    name_en: z
      .string({
        required_error: `${t("gloabl.name")} ${t(
          "errorMessage.fieldRequired"
        )}.`,
      })
      .min(2, {
        message: `${t("gloabl.name")} ${t("errorMessage.fieldCharsLength")}.`,
      }),
    price: z.union([
      z
        .string()
        .refine((val) => !isNaN(Number(val)))
        .transform((val) => Number(val)),
      z.number(),
    ]),
  });

  // 1. Define your form.
  const form = useForm({
    resolver: zodResolver(formSchema),

    defaultValues: {
      ...props.datasource,
    },
  });

  const onSubmit = (values) => {
    if (!props.editMode) {
      delete values.id;
    }

    props.onSubmit(values);
    if (dialogCloseRef.current) {
      dialogCloseRef.current.click();
    }
  };
  const onErorr = (errors) => {
    toast.error(t("errorMessage.fillAll"));
  };
  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit, onErorr)}
        className="space-y-6"
      >
        {/* Basic Information Card */}
        <Card className="border-2">
          <CardHeader className="pb-3 border-b">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <List className="w-5 h-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-lg">{t("gloabl.basic_information") || "Basic Information"}</CardTitle>
                <CardDescription>
                  {t("gloabl.basic_information_desc") || "Enter the sub-option name in both languages"}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            <MenuTabs form={form} className="bg-transparent" />
          </CardContent>
        </Card>

        {/* Price Card */}
        <Card className="border-2">
          <CardHeader className="pb-3 border-b">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <DollarSign className="w-5 h-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-lg">{t("gloabl.price")}</CardTitle>
                <CardDescription>
                  {t("gloabl.sub_option_price_desc") || "Set the additional price for this sub-option (0 for free)"}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            <FormField
              control={form.control}
              name="price"
              render={({ field }) => (
                <FormFieldItem label={t("gloabl.price")} form={form}>
                  <Input 
                    type="number" 
                    step="0.01"
                    min="0"
                    placeholder="0.00" 
                    {...field} 
                  />
                </FormFieldItem>
              )}
            />
          </CardContent>
        </Card>

        <Separator />

        {/* Actions */}
        <div className="flex items-center justify-end gap-3">
          <DialogClose asChild>
            <Button type="button" variant="outline">
              {t("gloabl.cancel")}
            </Button>
          </DialogClose>
          <DialogClose ref={dialogCloseRef} />
          <Button type="submit">{t("gloabl.save")}</Button>
        </div>
      </form>
    </Form>
  );
}

export default SubOptionForm;
